import React from 'react'
import type { ApplicationFieldMappingTestResult } from '../types'

interface TestMappingsModalProps {
  results: ApplicationFieldMappingTestResult
  onClose: () => void
}

const TestMappingsModal: React.FC<TestMappingsModalProps> = ({ results, onClose }) => {
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const getStatusIcon = (valid: boolean): string => {
    return valid ? '✅' : '❌'
  }

  const getStatusClass = (valid: boolean): string => {
    return valid ? 'test-result-success' : 'test-result-error'
  }

  const validCount = results.results.filter(r => r.valid).length
  const totalCount = results.results.length

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content test-results-modal">
        <div className="modal-header">
          <h3>Field Mapping Test Results</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Summary */}
          <div className={`test-summary ${results.success ? 'success' : 'warning'}`}>
            <div className="summary-icon">
              {results.success ? '✅' : '⚠️'}
            </div>
            <div className="summary-content">
              <h4>{results.message}</h4>
              <p>
                {validCount} of {totalCount} field mappings passed validation
                {validCount === totalCount ? ' 🎉' : ''}
              </p>
            </div>
          </div>

          {/* Results List */}
          <div className="test-results-list">
            <h5>Detailed Results:</h5>
            
            {results.results.map((result, index) => (
              <div 
                key={`${result.formFieldName}-${index}`}
                className={`test-result-item ${getStatusClass(result.valid)}`}
              >
                <div className="result-header">
                  <span className="result-icon">
                    {getStatusIcon(result.valid)}
                  </span>
                  <div className="result-mapping">
                    <strong>{result.formFieldName}</strong>
                    <span className="mapping-arrow">→</span>
                    <span className="salesforce-mapping">
                      {result.salesforceObject}.{result.salesforceField}
                    </span>
                  </div>
                </div>

                {/* Warnings */}
                {result.warnings && result.warnings.length > 0 && (
                  <div className="result-warnings">
                    <strong>⚠️ Warnings:</strong>
                    <ul>
                      {result.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Errors */}
                {result.errors && result.errors.length > 0 && (
                  <div className="result-errors">
                    <strong>❌ Errors:</strong>
                    <ul>
                      {result.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Recommendations */}
          {!results.success && (
            <div className="test-recommendations">
              <h5>💡 Recommendations:</h5>
              <ul>
                <li>Review the error messages above to identify issues</li>
                <li>Ensure Salesforce field names are spelled correctly</li>
                <li>Verify that custom fields exist in your Salesforce org</li>
                <li>Check field type compatibility (e.g., text → text, number → number)</li>
                <li>Contact your Salesforce administrator if field access is restricted</li>
              </ul>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default TestMappingsModal
